/**
 * Loved-One Voice Panel
 *
 * Records a relative's voice so Yoluno can read stories aloud in it.
 *
 * The consent step is the point of this component, not a formality bolted on
 * the end: the person speaking must be the one who types their name and agrees,
 * and the exact wording they agree to comes from the server so what we store
 * matches what they read. Submit stays disabled until both are done.
 */

import { useCallback, useRef, useState } from 'react';
import { Mic, Square, Trash2, Upload, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useFamilyVoices, useCreateFamilyVoice, useDeleteFamilyVoice } from '@/hooks/queries';
import { ConfirmDialog } from '@/components/shared/dialogs/ConfirmDialog';
import { getErrorMessage } from '@/integrations/api/client';
import { toast } from 'sonner';

/** ElevenLabs wants clean speech; below this the clone quality falls apart. */
const MIN_SECONDS = 30;
const MAX_SECONDS = 180;

interface LovedOneVoicePanelProps {
  familyMemberId: string;
  familyMemberName: string;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function LovedOneVoicePanel({ familyMemberId, familyMemberName }: LovedOneVoicePanelProps) {
  const { data, isLoading } = useFamilyVoices();
  const createVoice = useCreateFamilyVoice();
  const deleteVoice = useDeleteFamilyVoice();

  const [samples, setSamples] = useState<File[]>([]);
  const [consentName, setConsentName] = useState('');
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const existing = data?.items.find((v) => v.family_member_id === familyMemberId);
  const configured = data?.configured ?? false;

  const handleRecordingComplete = useCallback((blob: Blob) => {
    const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
    setSamples((prev) => [
      ...prev,
      new File([blob], `recording-${prev.length + 1}.${ext}`, { type: blob.type }),
    ]);
  }, []);

  const recorder = useAudioRecorder({
    onRecordingComplete: handleRecordingComplete,
    maxDuration: MAX_SECONDS * 1000,
    onError: (err) => toast.error(err.message),
  });

  const totalSamples = samples.length;
  const canSubmit = totalSamples > 0 && consentAgreed && consentName.trim().length > 1;

  const submit = () => {
    createVoice.mutate(
      { familyMemberId, samples, consentGivenBy: consentName.trim() },
      {
        onSuccess: () => {
          toast.success(`${familyMemberName}'s voice is ready to read stories.`);
          setSamples([]);
          setConsentName('');
          setConsentAgreed(false);
          recorder.reset();
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    );
  };

  const remove = () => {
    if (!existing) return;
    deleteVoice.mutate(existing.id, {
      onSuccess: () => {
        toast.success('Voice deleted.');
        setConfirmingDelete(false);
      },
      onError: (err) => toast.error(getErrorMessage(err)),
    });
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  if (!configured) {
    return (
      <div className="flex items-start gap-2.5 rounded-xl bg-secondary p-4">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Loved-one voices aren't available on this server yet.
        </p>
      </div>
    );
  }

  // ─── Already has a voice ───
  if (existing) {
    return (
      <>
        <div className="flex flex-col gap-3 rounded-xl border border-border p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {existing.family_member_name || existing.display_name} can read stories
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Consent given by {existing.consent_given_by} on{' '}
                  {new Date(existing.consent_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              disabled={deleteVoice.isPending}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Remove
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Pick this voice from the narrator menu when reading a story.
          </p>
        </div>

        <ConfirmDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          title={`Remove ${existing.family_member_name || existing.display_name}'s voice?`}
          description="The voice copy is permanently deleted, here and at our voice provider. Stories will fall back to a Yoluno reader. You can record a new one anytime."
          confirmLabel={deleteVoice.isPending ? 'Removing…' : 'Remove voice'}
          onConfirm={remove}
          variant="destructive"
        />
      </>
    );
  }

  // ─── Create flow ───
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm text-foreground">
          Record {familyMemberName} reading anything at all for {MIN_SECONDS} seconds or more — a
          favourite poem, a page of a book, how their day went. Yoluno can then read new stories
          aloud in their voice.
        </p>
        <p className="mt-1.5 text-xs text-muted-foreground">
          A quiet room and a normal speaking pace give the best result.
        </p>
      </div>

      {/* Recorder */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-secondary p-4">
        {recorder.isRecording ? (
          <>
            <Button variant="destructive" size="sm" onClick={recorder.stopRecording}>
              <Square className="mr-1.5 h-3.5 w-3.5" />
              Stop
            </Button>
            <span className="text-sm tabular-nums text-foreground">
              {formatDuration(recorder.duration)}
            </span>
            {recorder.duration < MIN_SECONDS && (
              <span className="text-xs text-muted-foreground">
                {MIN_SECONDS - recorder.duration}s more for a good result
              </span>
            )}
          </>
        ) : (
          <>
            <Button variant="default" size="sm" onClick={recorder.startRecording}>
              <Mic className="mr-1.5 h-3.5 w-3.5" />
              Record
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Upload audio
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length) setSamples((prev) => [...prev, ...picked].slice(0, 5));
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {recorder.error && <p className="text-sm text-destructive">{recorder.error}</p>}

      {/* Collected samples */}
      {totalSamples > 0 && (
        <ul className="flex flex-col gap-2">
          {samples.map((file, i) => (
            <li
              key={`${file.name}-${i}`}
              className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
            >
              <span className="truncate text-sm text-foreground">{file.name}</span>
              <button
                type="button"
                onClick={() => setSamples((prev) => prev.filter((_, idx) => idx !== i))}
                className="text-muted-foreground transition hover:text-destructive"
                aria-label={`Remove ${file.name}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Consent — required, and shown in the words the server stores */}
      {totalSamples > 0 && (
        <div className="flex flex-col gap-3 rounded-xl border border-border p-4">
          <p className="text-sm font-semibold text-foreground">
            {familyMemberName} needs to agree to this
          </p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {data?.consent_statement}
          </p>

          <label className="flex cursor-pointer items-start gap-2.5">
            <Checkbox
              checked={consentAgreed}
              onCheckedChange={(v) => setConsentAgreed(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-foreground">
              I am the person speaking, and I agree to the above.
            </span>
          </label>

          <div>
            <label
              htmlFor="consent-name"
              className="mb-1.5 block text-xs font-medium text-muted-foreground"
            >
              Type your name to sign
            </label>
            <Input
              id="consent-name"
              value={consentName}
              onChange={(e) => setConsentName(e.target.value)}
              placeholder="Full name"
              autoComplete="off"
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={!canSubmit || createVoice.isPending}>
          {createVoice.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Creating voice…
            </>
          ) : (
            'Create voice'
          )}
        </Button>
        {createVoice.isPending && (
          <span className="text-xs text-muted-foreground">This takes up to a minute.</span>
        )}
      </div>
    </div>
  );
}

export default LovedOneVoicePanel;
